/**
 * KullaniciTalebi.jsx — "Kullanıcı Ekleme Talepleri", the company's side (backlog #613; backend #612; staff side #614).
 *
 * Firma Kartı > Company Representatives > Users: a Katılımcı or Dekoratör company cannot create logins itself (#409 D4 / #418), it asks SAKA.
 * Three parts around one hook, so the page only wires them:
 *   useKullaniciTalebi(firmaId, { etkin })  the requests, may the caller send one now (`talepEdebilir` / `engel`), the roles to offer
 *   KullaniciTalepDugmesi                   the subtle red "Add user" button for the section's corner (opens the form)
 *   KullaniciTalepModal                     the form: name, surname, e-mail, the login name wanted, phone, title, portal role
 *   KullaniciTalepleriListesi               the company's own requests with their status, the rejection reason, and "withdraw" on a pending one
 * Who may send is decided by the backend (a login whose role reaches FIRMA_PROFILI at WRITE, at a fair where the company is Katılımcı or
 * Dekoratör, at most 5 pending); the page hides the button below WRITE like its Edit buttons, and a refusal that remains (the cap) shows as a
 * disabled button with the backend's reason.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap";
import { UserPlus, Undo2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import {
  getKullaniciTalepleri, gonderKullaniciTalebi, geriCekKullaniciTalebi, talepHatasi, TALEP_DURUMU,
} from "../../services/kullaniciTalebiService";

const EPOSTA = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const BOS_FORM = { ad: "", soyad: "", eposta: "", kullaniciAdi: "", telefon: "", unvan: "", portalRolId: "" };

/** The form's problems, as locale keys under `kullaniciTalebi.` (the same few rules the backend checks; it still has the last word). */
export function formHatalari(form) {
  const out = [];
  if (!form.ad.trim()) out.push("adGerekli");
  if (!form.soyad.trim()) out.push("soyadGerekli");
  if (!EPOSTA.test(form.eposta.trim())) out.push("epostaGecersiz");
  if (!form.kullaniciAdi.trim()) out.push("kullaniciAdiGerekli");
  else if (/\s/.test(form.kullaniciAdi.trim())) out.push("kullaniciAdiBosluk");
  return out;
}

/** Status name colours: pending amber, approved green, rejected red, withdrawn grey. Not reactstrap <Badge>: this theme paints it wrong. Shared with the staff list (#614). */
export const DURUM_RENK = {
  [TALEP_DURUMU.BEKLIYOR]: STATUS_HEX.warning,
  [TALEP_DURUMU.ONAYLANDI]: STATUS_HEX.success,
  [TALEP_DURUMU.REDDEDILDI]: STATUS_HEX.danger,
  [TALEP_DURUMU.GERI_CEKILDI]: NEUTRAL_GRAY,
};

// ─── The state ───────────────────────────────────────────────────────────────

/**
 * @param etkin  false = nothing is asked (a set that cannot send, no company yet)
 * @param fuarId the fair selected in the top bar: whether a request may be sent is judged at the login's selected fair, so a change asks again
 * @returns { veri, hata, yenile, modalAcik, setModalAcik, sonuc, setSonuc } — `veri` is null until the first answer (or when it failed:
 *          the section then simply has no button, the rest of the page is untouched).
 */
export function useKullaniciTalebi(firmaId, { etkin = true, fuarId = null } = {}) {
  const { i18n: i18nHook } = useTranslation("portal");
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [token, setToken] = useState(0);
  const [modalAcik, setModalAcik] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const dil = i18nHook?.language;

  // The fetch lives in the effect (react-hooks/set-state-in-effect). The answer's texts are in the request language, so a language switch asks again.
  useEffect(() => {
    if (!etkin || !firmaId) return undefined;
    let ignore = false;
    async function run() {
      try {
        const cevap = await getKullaniciTalepleri(firmaId);
        if (ignore) return;
        setVeri(cevap);
        setHata(null);
      } catch (e) {
        if (ignore) return;
        setVeri(null);
        setHata(talepHatasi(e));
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId, etkin, fuarId, token, dil]);

  return { veri, hata, yenile: () => setToken((t) => t + 1), modalAcik, setModalAcik, sonuc, setSonuc };
}

// ─── The button ──────────────────────────────────────────────────────────────

/** Subtle red (outline) like the section's other quiet actions; disabled with the backend's reason when the caller cannot send now. */
export function KullaniciTalepDugmesi({ talep, onAc }) {
  const { t } = useTranslation("portal");
  if (!talep?.veri) return null;
  const { talepEdebilir, engel } = talep.veri;
  const ipucu = talepEdebilir ? t("kullaniciTalebi.dugmeIpucu") : (engel || t("kullaniciTalebi.gonderilemez"));
  return (
    <span title={ipucu}>
      <Button color="danger" outline size="sm" disabled={!talepEdebilir} onClick={() => { talep.setModalAcik(true); onAc?.(); }}>
        <UserPlus size={14} className="me-1" />{t("kullaniciTalebi.kullaniciEkle")}
      </Button>
    </span>
  );
}

// ─── The form ────────────────────────────────────────────────────────────────

export function KullaniciTalepModal({ firmaId, talep }) {
  const { t } = useTranslation("portal");
  const roller = talep.veri?.roller || [];
  const [form, setForm] = useState(BOS_FORM);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [denendi, setDenendi] = useState(false);

  const varsayilanRol = roller.find((r) => r.varsayilan)?.portalRolId ?? roller[0]?.portalRolId ?? "";
  const rolId = form.portalRolId === "" ? varsayilanRol : form.portalRolId;
  const sorunlar = formHatalari(form);

  function ayarla(alan, deger) {
    setForm((f) => ({ ...f, [alan]: deger }));
  }

  function kapat() {
    if (gonderiliyor) return;
    talep.setModalAcik(false);
  }

  // A fresh form every time the window opens (a sent request must not come back as the next draft).
  function acildi() {
    setForm(BOS_FORM);
    setHata(null);
    setDenendi(false);
  }

  async function gonder() {
    setDenendi(true);
    if (sorunlar.length > 0) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      const cevap = await gonderKullaniciTalebi(firmaId, { ...form, portalRolId: rolId });
      talep.setSonuc({ mesaj: cevap?.mesaj || t("kullaniciTalebi.gonderildi"), uyari: cevap?.uyari || null });
      talep.setModalAcik(false);
      talep.yenile();
    } catch (e) {
      setHata(talepHatasi(e) || t("kullaniciTalebi.gonderilemedi"));
    } finally {
      setGonderiliyor(false);
    }
  }

  const alan = (anahtar, etiket, { zorunlu = false, tip = "text", hataAnahtari = null, placeholder = "" } = {}) => (
    <div className="mb-2">
      <Label className="small mb-1" for={`kullanici-talebi-${anahtar}`}>{etiket}{zorunlu ? " *" : ""}</Label>
      <Input
        id={`kullanici-talebi-${anahtar}`} bsSize="sm" type={tip} value={form[anahtar]} placeholder={placeholder}
        invalid={denendi && !!hataAnahtari && sorunlar.includes(hataAnahtari)} maxLength={255}
        onChange={(e) => ayarla(anahtar, e.target.value)}
      />
    </div>
  );

  return (
    <Modal isOpen={talep.modalAcik} toggle={kapat} onEnter={acildi} centered>
      <ModalHeader toggle={kapat}>{t("kullaniciTalebi.baslik")}</ModalHeader>
      <ModalBody>
        <p className="text-muted small">{t("kullaniciTalebi.aciklama")}</p>
        {hata && <Alert color="danger" className="py-2 small">{hata}</Alert>}
        <Row>
          <Col md={6}>{alan("ad", t("kullaniciTalebi.ad"), { zorunlu: true, hataAnahtari: "adGerekli" })}</Col>
          <Col md={6}>{alan("soyad", t("kullaniciTalebi.soyad"), { zorunlu: true, hataAnahtari: "soyadGerekli" })}</Col>
        </Row>
        {alan("eposta", t("kullaniciTalebi.eposta"), { zorunlu: true, tip: "email", hataAnahtari: "epostaGecersiz" })}
        {alan("kullaniciAdi", t("kullaniciTalebi.kullaniciAdi"), {
          zorunlu: true, hataAnahtari: sorunlar.includes("kullaniciAdiBosluk") ? "kullaniciAdiBosluk" : "kullaniciAdiGerekli",
          placeholder: t("kullaniciTalebi.kullaniciAdiOrnek"),
        })}
        <div className="small text-muted mb-2">{t("kullaniciTalebi.kullaniciAdiNotu")}</div>
        <Row>
          <Col md={6}>{alan("telefon", t("kullaniciTalebi.telefon"), { tip: "tel" })}</Col>
          <Col md={6}>{alan("unvan", t("kullaniciTalebi.unvan"))}</Col>
        </Row>
        {roller.length > 1 && (
          <div className="mb-2">
            <Label className="small mb-1" for="kullanici-talebi-rol">{t("kullaniciTalebi.rol")}</Label>
            <Input
              id="kullanici-talebi-rol" bsSize="sm" type="select" value={rolId}
              onChange={(e) => ayarla("portalRolId", Number(e.target.value))}
            >
              {roller.map((r) => <option key={r.portalRolId} value={r.portalRolId}>{r.ad}</option>)}
            </Input>
          </div>
        )}
        {denendi && sorunlar.length > 0 && (
          <ul className="small text-danger ps-3 mb-0">
            {sorunlar.map((k) => <li key={k}>{t(`kullaniciTalebi.${k}`)}</li>)}
          </ul>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" onClick={kapat} disabled={gonderiliyor}>{t("common:iptal")}</Button>
        <Button color="danger" size="sm" onClick={gonder} disabled={gonderiliyor}>
          {gonderiliyor ? <Spinner size="sm" className="me-1" /> : <UserPlus size={14} className="me-1" />}
          {t("kullaniciTalebi.talepGonder")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── The company's requests ──────────────────────────────────────────────────

function tarihMetni(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(currentLocaleTag()); } catch { return "—"; }
}

/** The result of the last send (and the e-mail warning), then the table. Nothing at all while the company has sent no request. */
export function KullaniciTalepleriListesi({ firmaId, talep }) {
  const { t } = useTranslation("portal");
  const [geriCekilecek, setGeriCekilecek] = useState(null);
  const [geriCekiliyor, setGeriCekiliyor] = useState(false);
  const [hata, setHata] = useState(null);
  const talepler = talep?.veri?.talepler || [];
  const sonuc = talep?.sonuc;

  if (!sonuc && talepler.length === 0 && !hata) return null;

  async function geriCek() {
    if (!geriCekilecek) return;
    setGeriCekiliyor(true);
    setHata(null);
    try {
      const cevap = await geriCekKullaniciTalebi(firmaId, geriCekilecek.id);
      talep.setSonuc({ mesaj: cevap?.mesaj || t("kullaniciTalebi.geriCekildi"), uyari: null });
      setGeriCekilecek(null);
      talep.yenile();
    } catch (e) {
      setHata(talepHatasi(e) || t("kullaniciTalebi.geriCekilemedi"));
      setGeriCekilecek(null);
    } finally {
      setGeriCekiliyor(false);
    }
  }

  return (
    <div className="mt-2" data-testid="kullanici-talepleri">
      {sonuc && (
        <Alert color={sonuc.uyari ? "warning" : "success"} className="py-2 small" toggle={() => talep.setSonuc(null)}>
          <div>{sonuc.mesaj}</div>
          {sonuc.uyari && <div className="mt-1">{sonuc.uyari}</div>}
        </Alert>
      )}
      {hata && <Alert color="danger" className="py-2 small" toggle={() => setHata(null)}>{hata}</Alert>}
      {talepler.length > 0 && (
        <>
          <div className="text-muted small text-uppercase fw-semibold mb-1">{t("kullaniciTalebi.taleplerim")}</div>
          <div className="table-responsive">
            <Table className="align-middle mb-0" size="sm">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th>{t("kullaniciTalebi.kisi")}</th>
                  <th>{t("kullaniciTalebi.kullaniciAdi")}</th>
                  <th>{t("kullaniciTalebi.rol")}</th>
                  <th>{t("kullaniciTalebi.tarih")}</th>
                  <th>{t("kullaniciTalebi.durum")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {talepler.map((r) => (
                  <tr key={r.id}>
                    <td className="small">
                      <div className="fw-semibold">{`${r.ad || ""} ${r.soyad || ""}`.trim()}</div>
                      <div className="text-muted">{r.eposta}</div>
                    </td>
                    <td className="small">{r.kullaniciAdi}</td>
                    <td className="small">{r.portalRolAdi || "—"}</td>
                    <td className="small">{tarihMetni(r.olusturmaTarihi)}</td>
                    <td className="small">
                      <span className="badge text-white fw-normal" style={{ backgroundColor: DURUM_RENK[r.durum] ?? NEUTRAL_GRAY }}>
                        {r.durumAdi}
                      </span>
                      {r.durum === TALEP_DURUMU.REDDEDILDI && r.redSebebi && (
                        <div className="text-danger mt-1">{t("kullaniciTalebi.redSebebi")}: {r.redSebebi}</div>
                      )}
                    </td>
                    <td className="text-end">
                      {r.durum === TALEP_DURUMU.BEKLIYOR && (
                        <Button color="light" outline size="sm" onClick={() => setGeriCekilecek(r)} title={t("kullaniciTalebi.geriCek")}>
                          <Undo2 size={14} className="me-1" />{t("kullaniciTalebi.geriCek")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
      <Modal isOpen={!!geriCekilecek} toggle={() => !geriCekiliyor && setGeriCekilecek(null)} centered size="sm">
        <ModalHeader toggle={() => !geriCekiliyor && setGeriCekilecek(null)}>{t("kullaniciTalebi.geriCekBaslik")}</ModalHeader>
        <ModalBody className="small">
          {t("kullaniciTalebi.geriCekSorusu", { ad: `${geriCekilecek?.ad || ""} ${geriCekilecek?.soyad || ""}`.trim() })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setGeriCekilecek(null)} disabled={geriCekiliyor}>{t("common:iptal")}</Button>
          <Button color="danger" size="sm" onClick={geriCek} disabled={geriCekiliyor}>
            {geriCekiliyor && <Spinner size="sm" className="me-1" />}{t("kullaniciTalebi.geriCek")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

